export type EditorRole = "editor" | "reviewer" | "administrator";
export type ContentType = "brand" | "bottle" | "guide";
export type PublicationStatus =
  | "draft"
  | "in_review"
  | "published"
  | "correction_required";
export type WorkflowAction =
  | "submit_for_review"
  | "publish"
  | "request_correction"
  | "rollback";

export interface EditorialSession {
  user: {
    id: string;
    email?: string | null;
  };
  role: EditorRole;
}

export interface EditorialRecord {
  id: string;
  name?: string;
  title?: string;
  slug: string;
  publicationStatus?: PublicationStatus;
  publication_status?: PublicationStatus;
  abv?: number | null;
  volumeMl?: number | null;
  lastReviewed?: string;
  last_reviewed?: string;
  updatedAt?: string;
  updated_at?: string;
}

export interface EditorialContent {
  brands: EditorialRecord[];
  bottles: EditorialRecord[];
  guides: EditorialRecord[];
}

// Bottle drafts always state abv and volume_ml: null when the source does not
// list them, never 0. The API requires both on updates.
export interface BottleDraftData {
  brand_id: string;
  name: string;
  slug: string;
  category: string;
  origin: string;
  description: string;
  abv: number | null;
  volume_ml: number | null;
  editorial_status: string;
  last_reviewed: string;
}

export interface DraftPayload {
  type: ContentType;
  data: Record<string, unknown>;
}

export type EditorialStatus =
  | "editorially-sourced"
  | "user-reported-unverified"
  | "official-record-link-available"
  | "official-response-received";

// GET /api/editorial/content/:type/:id. `record` is exactly a PATCH body for
// /api/editorial/drafts: the PATCH is a full replace with a strict schema, so
// edit fields inside record.data and send the whole record back, with no
// extra keys. `meta` is read-only display.
export interface BrandDraftFields {
  name: string;
  slug: string;
  aliases: string[];
  category: string;
  origin: string;
  history: string;
  editorial_status: EditorialStatus;
  last_reviewed: string;
}

export interface BottleDraftFields {
  brand_id: string;
  name: string;
  slug: string;
  category: string;
  origin: string;
  description: string;
  abv: number | null;
  volume_ml: number | null;
  editorial_status: EditorialStatus;
  last_reviewed: string;
}

export interface GuideDraftFields {
  title: string;
  slug: string;
  summary: string;
  body: string;
  editorial_status: EditorialStatus;
  last_reviewed: string;
}

export type DraftUpdatePayload =
  | { id: string; type: "brand"; data: BrandDraftFields }
  | { id: string; type: "bottle"; data: BottleDraftFields }
  | { id: string; type: "guide"; data: GuideDraftFields };

export interface EditorialRecordMeta {
  publication_status: PublicationStatus;
  updated_at: string;
  brand_name?: string;
}

export interface EditorialCitationDetail {
  source_id: string;
  slug: string;
  publisher: string;
  title: string;
  url: string;
  accessed_at: string;
}

export interface EditorialRecordDetail {
  record: DraftUpdatePayload;
  meta: EditorialRecordMeta;
  citations: EditorialCitationDetail[];
}

export interface CitationPayload {
  contentType: ContentType;
  contentId: string;
  source: {
    slug: string;
    publisher: string;
    title: string;
    url: string;
    accessedAt: string;
  };
}

export interface TransitionPayload {
  contentType: ContentType;
  contentId: string;
  action: WorkflowAction;
  reason: string | null;
}

type TokenProvider = () => Promise<string | null>;

class EditorialApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EditorialApiError";
  }
}

export function hasEditorialApiConfiguration() {
  return Boolean(process.env.REACT_APP_EDITORIAL_API_URL);
}

export function hasClerkConfiguration() {
  return Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);
}

function getApiUrl(path: string) {
  const baseUrl = process.env.REACT_APP_EDITORIAL_API_URL;

  if (!baseUrl) {
    throw new EditorialApiError(
      "Set REACT_APP_EDITORIAL_API_URL to use the editorial workspace.",
    );
  }

  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

async function getErrorMessage(response: Response) {
  try {
    const body: unknown = await response.json();

    if (
      body &&
      typeof body === "object" &&
      "error" in body &&
      typeof body.error === "string"
    ) {
      return body.error;
    }

    if (
      body &&
      typeof body === "object" &&
      "message" in body &&
      typeof body.message === "string"
    ) {
      return body.message;
    }
  } catch {
    // A non-JSON error response still receives a useful status message.
  }

  return `Editorial API request failed (${response.status}).`;
}

function unwrapResponse<T>(body: unknown): T {
  if (body && typeof body === "object" && "data" in body) {
    return body.data as T;
  }

  return body as T;
}

export function createEditorialApiClient(getToken: TokenProvider) {
  async function request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const token = await getToken();

    if (!token) {
      throw new EditorialApiError("Your session has expired.");
    }

    const response = await fetch(getApiUrl(path), {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new EditorialApiError(await getErrorMessage(response));
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return unwrapResponse<T>(await response.json());
  }

  return {
    getSession: () => request<EditorialSession>("/api/editorial/session"),
    getContent: () => request<EditorialContent>("/api/editorial/content"),
    createDraft: (payload: DraftPayload) =>
      request<void>("/api/editorial/drafts", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    getRecordDetail: (type: ContentType, id: string) =>
      request<EditorialRecordDetail>(
        `/api/editorial/content/${type}/${encodeURIComponent(id)}`,
      ),
    updateDraft: (payload: DraftUpdatePayload) =>
      request<void>("/api/editorial/drafts", {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    addCitation: (payload: CitationPayload) =>
      request<void>("/api/editorial/citations", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    transition: (payload: TransitionPayload) =>
      request<void>("/api/editorial/transitions", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  };
}
